import { AnalyticsType } from "@src/appV2/Analytics/constants";
import { type HelpCenterEntry } from "@src/appV2/HelpCenter/useHelpCenterEntry";
import { getLocation, routes } from "@src/appV2/redesign/utils/routes";

import { type WorkplaceMenuPrivileges } from "../privilegesProviders/WorkplaceMenuPrivilegesProvider";
import {
  DEFAULT_NAVIGATION_TRANSLATION_LABELS,
  type NavigationTranslationLabels,
} from "./translationLabels";
import { type NavigationItem, type NavigationMenu, type NavigationSection } from "./types";
import { getRouteAndSelected, isPathSelected } from "./utils";

interface AnalyticsConfig {
  type: AnalyticsType;
  embedId: string;
}

interface WorkplaceNavigationOptions {
  unreadChannelCount?: number;
  pendingExtraWorkedTimeRequestCount?: number;
  pendingVisitOccurrenceCount?: number;
  isMobile?: boolean;
  isClassicCalendarPathActive?: boolean;
  onSchedulePreload?: () => void | Promise<void>;
  translationLabels?: NavigationTranslationLabels;
  helpCenterEntry?: HelpCenterEntry;
}

export function getWorkplaceNavigationMenu(
  workplaceMenuPrivileges: WorkplaceMenuPrivileges,
  currentPath: string,
  analyticsConfig: AnalyticsConfig[] = [],
  options: WorkplaceNavigationOptions = {}
): NavigationMenu {
  const {
    unreadChannelCount,
    pendingExtraWorkedTimeRequestCount,
    pendingVisitOccurrenceCount,
    isMobile,
    isClassicCalendarPathActive,
    onSchedulePreload,
    translationLabels = DEFAULT_NAVIGATION_TRANSLATION_LABELS,
    helpCenterEntry,
  } = options;
  const items: Array<NavigationItem | NavigationSection> = [];

  // Home Health specific items (shown at top for Home Health workplaces)
  if (workplaceMenuPrivileges.homeHealthSection) {
    if (!isMobile && workplaceMenuPrivileges.homeHealthSection.cases) {
      items.push({
        label: translationLabels.cases,
        iconType: "profile",
        ...getRouteAndSelected("homeHealthCases", currentPath),
      });
    }
    if (workplaceMenuPrivileges.homeHealthSection.visitVerification) {
      items.push({
        label: translationLabels.visitVerification,
        iconType: "badge-check",
        ...getRouteAndSelected(
          isMobile ? "homeHealthVisitVerificationMobile" : "homeHealthVisitVerification",
          currentPath
        ),
        badgeCount: pendingVisitOccurrenceCount,
      });
    }
    if (workplaceMenuPrivileges.homeHealthSection.coverageMap) {
      items.push({
        label: translationLabels.coverageMap,
        iconType: "location-alt",
        ...getRouteAndSelected("homeHealthCoverageMap", currentPath),
      });
    }
    if (workplaceMenuPrivileges.homeHealthSection.connectEmr) {
      items.push({
        label: translationLabels.connectEmr,
        iconType: "hyperlink",
        ...getRouteAndSelected("homeHealthConnectEmr", currentPath),
        badgeLabel: translationLabels.newBadge,
      });
    }
  }

  if (workplaceMenuPrivileges.workplaceOnboarding) {
    items.push({
      label: translationLabels.gettingStarted,
      iconType: "hybrid-view",
      ...getRouteAndSelected("facilityOnboarding", currentPath),
    });
  }

  if (workplaceMenuPrivileges.dailyView) {
    items.push({
      label: translationLabels.schedule,
      iconType: "calendar-day",
      routeTo: { pathname: routes.schedule },
      isSelected: isPathSelected(currentPath, routes.schedule),
      onPreload: onSchedulePreload,
    });
  }

  if (!isMobile && workplaceMenuPrivileges.calendar) {
    // While the /facility/dashboard → /schedule redirect is active, the legacy
    // classic calendar lives at /calendar-classic. For workplaces without the
    // redirect, the legacy view still renders at /facility/dashboard, so point
    // the sidebar at the path that will actually render to keep the item's
    // selected state in sync with the URL.
    items.push({
      label: translationLabels.calendarClassic,
      iconType: "calendar",
      ...getRouteAndSelected(
        isClassicCalendarPathActive ? "calendarClassic" : "facilityDashboard",
        currentPath
      ),
    });
  }

  if (workplaceMenuPrivileges.chat) {
    const chatRouteKey = workplaceMenuPrivileges.chatRedesign ? "chat" : "facilityChat";
    items.push({
      label: translationLabels.chat,
      iconType: "message",
      ...getRouteAndSelected(chatRouteKey, currentPath),
      badgeCount: unreadChannelCount,
    });
  }

  if (workplaceMenuPrivileges.professionalsSection) {
    const sectionItems: NavigationItem[] = [];

    if (workplaceMenuPrivileges.professionalsSection.myProfessionals) {
      sectionItems.push({
        label: translationLabels.myProfessionals,
        iconType: "folder-person",
        ...getRouteAndSelected("workplaceWorkers", currentPath),
      });
    }
    if (!isMobile && workplaceMenuPrivileges.professionalsSection.browseProfessionals) {
      sectionItems.push({
        label: translationLabels.browseProfessionals,
        iconType: "people",
        ...getRouteAndSelected("workersDirectory", currentPath),
      });
    }

    items.push({ label: translationLabels.professionals, items: sectionItems });
  }

  if (
    workplaceMenuPrivileges.permanentHiringSection &&
    !workplaceMenuPrivileges.isPermanentHiringSectionLoading
  ) {
    const sectionItems: NavigationItem[] = [];

    if (workplaceMenuPrivileges.permanentHiringSection.placementsLandingPage) {
      sectionItems.push({
        label: translationLabels.landingPage,
        iconType: "ribbon",
        ...getRouteAndSelected("jobsLandingPage", currentPath),
      });
    }
    if (workplaceMenuPrivileges.permanentHiringSection.interviewSchedule) {
      sectionItems.push({
        label: translationLabels.interviewSchedule,
        iconType: "clock-outlined",
        ...getRouteAndSelected("interviewSchedule", currentPath),
      });
    }
    if (workplaceMenuPrivileges.permanentHiringSection.applicationAndJobs) {
      sectionItems.push({
        label: translationLabels.applicationAndJobs,
        iconType: "briefcase",
        ...getRouteAndSelected("manageJobs", currentPath),
      });
    }
    if (workplaceMenuPrivileges.permanentHiringSection.browseTalentPool) {
      sectionItems.push({
        label: translationLabels.browseTalentPool,
        iconType: "person-superstar",
        ...getRouteAndSelected("workplaceHireProfessionals", currentPath),
      });
    }
    if (workplaceMenuPrivileges.permanentHiringSection.interviewAvailability) {
      sectionItems.push({
        label: translationLabels.interviewAvailability,
        iconType: "person-male-formal",
        ...getRouteAndSelected("interviewSettings", currentPath),
      });
    }

    items.push({ label: translationLabels.permanentHiring, items: sectionItems });
  }

  if (workplaceMenuPrivileges.charting) {
    const sectionItems: NavigationItem[] = [];

    sectionItems.push({
      label: translationLabels.charting,
      iconType: "chart-line-increase",
      ...getRouteAndSelected("workplaceCharting", currentPath),
    });

    items.push({ label: translationLabels.charting, items: sectionItems });
  }

  if (workplaceMenuPrivileges.analyticsSection) {
    const sectionItems: NavigationItem[] = [];

    if (workplaceMenuPrivileges.analyticsSection.exploWorkplaceAnalytics) {
      const exploWorkplaceAnalyticsEmbedId = analyticsConfig.find(
        (config) => config.type === AnalyticsType.WORKPLACE_FLEX
      )?.embedId;
      if (exploWorkplaceAnalyticsEmbedId) {
        sectionItems.push({
          label: translationLabels.workplaceAnalytics,
          iconType: "microscope",
          routeTo: getLocation("workplaceAnalytics", {
            queryParams: { embedId: exploWorkplaceAnalyticsEmbedId },
          }),
          isSelected: currentPath.includes(routes.workplaceAnalytics),
        });
      }
    }

    // Only show corporate analytics in Analytics section when Corporate Center is not visible
    if (
      !workplaceMenuPrivileges.corporateCenterSection &&
      workplaceMenuPrivileges.analyticsSection.exploCorporateAnalytics
    ) {
      const exploCorporateAnalyticsEmbedId = analyticsConfig.find(
        (config) => config.type === AnalyticsType.CORPORATE_FLEX
      )?.embedId;
      if (exploCorporateAnalyticsEmbedId) {
        sectionItems.push({
          label: translationLabels.corporateAnalytics,
          iconType: "binoculars",
          routeTo: getLocation("workplaceAnalytics", {
            queryParams: { embedId: exploCorporateAnalyticsEmbedId },
          }),
          isSelected: currentPath.includes(routes.workplaceAnalytics),
        });
      }
    }

    if (sectionItems.length > 0) {
      items.push({ label: translationLabels.analytics, items: sectionItems });
    }
  }

  if (workplaceMenuPrivileges.corporateCenterSection) {
    const sectionItems: NavigationItem[] = [];

    if (workplaceMenuPrivileges.corporateCenterSection.exploCorporateAnalytics) {
      const exploCorporateAnalyticsEmbedId = analyticsConfig.find(
        (config) => config.type === AnalyticsType.CORPORATE_FLEX
      )?.embedId;
      if (exploCorporateAnalyticsEmbedId) {
        sectionItems.push({
          label: translationLabels.corporateAnalytics,
          iconType: "binoculars",
          routeTo: getLocation("workplaceAnalytics", {
            queryParams: { embedId: exploCorporateAnalyticsEmbedId },
          }),
          isSelected: currentPath.includes(routes.workplaceAnalytics),
        });
      }
    }

    if (workplaceMenuPrivileges.corporateCenterSection.workplaceAnalytics) {
      sectionItems.push({
        label: translationLabels.analytics,
        iconType: "binoculars",
        routeTo: getLocation("workplaceAnalyticsCorporate"),
        isSelected: currentPath.includes(routes.workplaceAnalyticsCorporate),
      });
    }

    if (workplaceMenuPrivileges.corporateCenterSection.spendingControls) {
      sectionItems.push({
        label: translationLabels.spendControls,
        iconType: "gauge",
        routeTo: getLocation("corporateSpendingControls"),
        isSelected: currentPath.includes(routes.corporateSpendingControls),
      });
    }

    if (sectionItems.length > 0) {
      items.push({ label: translationLabels.corporateCenter, items: sectionItems });
    }
  }

  if (workplaceMenuPrivileges.liveWorkerETA) {
    const sectionItems: NavigationItem[] = [];

    sectionItems.push({
      label: translationLabels.liveWorkerEta,
      iconType: "coordinates-person",
      ...getRouteAndSelected("facilityLiveWorkerETA", currentPath),
    });

    items.push({ label: translationLabels.liveWorkerEta, items: sectionItems });
  }

  if (workplaceMenuPrivileges.workplaceRulesQuiz) {
    items.push({
      label: translationLabels.workplaceRulesQuiz,
      iconType: "book",
      routeTo: getLocation("facilityWorkerInstructions", {
        queryParams: { click_src: "menu_item" },
      }),
      isSelected: currentPath.includes("/facility/worker-instructions"),
    });
  }

  if (workplaceMenuPrivileges.managementSection) {
    const sectionItems: NavigationItem[] = [];

    if (workplaceMenuPrivileges.managementSection.myAccount) {
      sectionItems.push({
        label: translationLabels.myAccount,
        iconType: "person-male",
        ...getRouteAndSelected("myAccount", currentPath),
      });
    }

    if (workplaceMenuPrivileges.managementSection.teamMembers) {
      sectionItems.push({
        label: translationLabels.teamMembers,
        iconType: "group",
        ...getRouteAndSelected("workplaceUsers", currentPath),
      });
    }

    if (workplaceMenuPrivileges.managementSection.internalRoster) {
      sectionItems.push({
        label: translationLabels.myInternalRoster,
        iconType: "folder-person",
        ...getRouteAndSelected("workplaceInternalRoster", currentPath),
      });
    }

    if (workplaceMenuPrivileges.managementSection.requiredDocuments) {
      sectionItems.push({
        label: translationLabels.workplaceRequirements,
        iconType: "documents",
        ...getRouteAndSelected("workplaceDocumentMapping", currentPath),
      });
    }

    if (workplaceMenuPrivileges.managementSection.workplaceInformation) {
      sectionItems.push({
        label: translationLabels.workplaceInformation,
        iconType: "information",
        ...getRouteAndSelected("facilityWorkerInstructions", currentPath),
      });
    }

    if (workplaceMenuPrivileges.managementSection.settings) {
      sectionItems.push({
        label: translationLabels.settings,
        iconType: "cogwheel",
        ...getRouteAndSelected("settings", currentPath),
      });
    }

    items.push({ label: translationLabels.management, items: sectionItems });
  }

  if (workplaceMenuPrivileges.billingSection) {
    const sectionItems: NavigationItem[] = [];

    if (workplaceMenuPrivileges.billingSection.reporting) {
      sectionItems.push({
        label: translationLabels.reporting,
        iconType: "chart-line-increase",
        ...getRouteAndSelected("facilityInvoiceReporting", currentPath),
      });
    }

    if (workplaceMenuPrivileges.billingSection.upcomingCharges) {
      sectionItems.push({
        label: translationLabels.upcomingCharges,
        iconType: "dollar",
        ...getRouteAndSelected("facilityInvoiceUpcomingCharges", currentPath),
        badgeCount: pendingExtraWorkedTimeRequestCount,
      });
    }

    if (workplaceMenuPrivileges.billingSection.workplaceSpendingControls) {
      sectionItems.push({
        label: translationLabels.spendControls,
        iconType: "gauge",
        ...getRouteAndSelected("workplaceSpendingControls", currentPath),
      });
    }

    if (workplaceMenuPrivileges.billingSection.invoiceHistory) {
      sectionItems.push({
        label: translationLabels.invoiceHistory,
        iconType: "money-dollar",
        ...getRouteAndSelected("facilityInvoiceHistory", currentPath),
      });
    }

    if (!isMobile && workplaceMenuPrivileges.billingSection.contractTerms) {
      sectionItems.push({
        label: translationLabels.contractTerms,
        iconType: "notepad-alt",
        ...getRouteAndSelected("facilityInvoiceContractTerms", currentPath),
      });
    }

    items.push({ label: translationLabels.billing, items: sectionItems });
  }

  if (workplaceMenuPrivileges.helpCenter) {
    const sectionItems: NavigationItem[] = [];

    const helpCenterItem: NavigationItem =
      helpCenterEntry?.kind === "action"
        ? {
            label: translationLabels.helpCenter,
            iconType: "question-circle",
            isSelected: false,
            onClick: helpCenterEntry.onClick,
          }
        : {
            label: translationLabels.helpCenter,
            iconType: "question-circle",
            ...getRouteAndSelected("helpCenter", currentPath),
          };

    sectionItems.push(helpCenterItem);

    items.push({ label: translationLabels.helpCenter, items: sectionItems });
  }

  if (workplaceMenuPrivileges.referAndEarn) {
    const sectionItems: NavigationItem[] = [];

    sectionItems.push({
      label: translationLabels.referAndEarn,
      iconType: "palm-dollar",
      ...getRouteAndSelected("workplaceReferAndEarn", currentPath),
    });

    items.push({ label: translationLabels.referAndEarn, items: sectionItems });
  }

  return { items };
}
